import React, { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Oblicza wszystkie komórki na linii od start do end.
 * Obsługuje: poziomo, pionowo, ukośnie (8 kierunków).
 * Jeśli end nie leży dokładnie na linii – zwraca najbliższy punkt.
 */
const getCellsInLine = (start, end) => {
    const dr = end.r - start.r;
    const dc = end.c - start.c;
    const absDr = Math.abs(dr);
    const absDc = Math.abs(dc);

    // Kierunek: snap do 4 osi (H, V, 2 przekątne)
    let stepR = 0, stepC = 0, steps = 0;

    if (absDr === 0 && absDc === 0) return [start];

    if (absDr === 0) {                              // poziomo
        stepC = dc > 0 ? 1 : -1; steps = absDc;
    } else if (absDc === 0) {                       // pionowo
        stepR = dr > 0 ? 1 : -1; steps = absDr;
    } else if (absDr === absDc) {                   // idealna przekątna
        stepR = dr > 0 ? 1 : -1;
        stepC = dc > 0 ? 1 : -1;
        steps = absDr;
    } else {                                        // snap do dominującej osi
        if (absDr >= absDc) {
            stepR = dr > 0 ? 1 : -1; steps = absDr;
        } else {
            stepC = dc > 0 ? 1 : -1; steps = absDc;
        }
    }

    const cells = [];
    for (let i = 0; i <= steps; i++) {
        cells.push({ r: start.r + stepR * i, c: start.c + stepC * i });
    }
    return cells;
};

const cellsToString = (cells, grid) =>
    cells.map(({ r, c }) => grid[r]?.[c] ?? '').join('');

export default function WordSearchView({ grid, words, onWin }) {
    const [foundWords, setFoundWords]         = useState(new Set());
    const [selection, setSelection]           = useState([]);
    const [confirmedLines, setConfirmedLines] = useState([]);
    const [hintedCell, setHintedCell]         = useState(null); // {r, c} | null
    const isSelecting = useRef(false);
    const startCell   = useRef(null);
    const hintTimer   = useRef(null);
    const scrollRAF   = useRef(null);

    // Auto-scroll podczas zaznaczania przy krawędzi ekranu
    useEffect(() => {
        const THRESHOLD = 80; // px od krawędzi
        const SPEED     = 1; // px na klatkę

        const onMouseMove = (e) => {
            if (!isSelecting.current) {
                if (scrollRAF.current) {
                    cancelAnimationFrame(scrollRAF.current);
                    scrollRAF.current = null;
                }
                return;
            }

            const y = e.clientY;
            const h = window.innerHeight;
            let dir = 0;
            if (y < THRESHOLD)      dir = -1;
            else if (y > h - THRESHOLD) dir = 1;

            if (dir === 0) {
                cancelAnimationFrame(scrollRAF.current);
                scrollRAF.current = null;
                return;
            }

            if (scrollRAF.current) return; // już scrolluje

            const tick = () => {
                window.scrollBy(0, dir * SPEED);
                scrollRAF.current = requestAnimationFrame(tick);
            };
            scrollRAF.current = requestAnimationFrame(tick);
        };

        const onMouseUp = () => {
            cancelAnimationFrame(scrollRAF.current);
            scrollRAF.current = null;
        };

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup',   onMouseUp);
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup',   onMouseUp);
            cancelAnimationFrame(scrollRAF.current);
        };
    }, []);

    // Sprawdzenie wygranej
    useEffect(() => {
        if (words.length > 0 && foundWords.size === words.length) {
            onWin?.();
        }
    }, [foundWords, words.length, onWin]);

    const showHint = (word) => {
        const r = (word.starty ?? 1) - 1;
        const c = (word.startx ?? 1) - 1;
        setHintedCell({ r, c });
        clearTimeout(hintTimer.current);
        hintTimer.current = setTimeout(() => setHintedCell(null), 2000);
    };

    const cellKey = (r, c) => `${r}-${c}`;

    const isInSelection = (r, c) =>
        selection.some(s => s.r === r && s.c === c);

    const isConfirmed = (r, c) =>
        confirmedLines.some(l => l.cells.some(s => s.r === r && s.c === c));

    const checkSelection = useCallback((cells) => {
        const forward = cellsToString(cells, grid);

        const matched = words.find(w => w.answer.toUpperCase() === forward);

        if (matched && !foundWords.has(matched.answer.toUpperCase())) {
            setFoundWords(prev => new Set([...prev, matched.answer.toUpperCase()]));
            setConfirmedLines(prev => [...prev, { cells, wordAnswer: matched.answer.toUpperCase() }]);
        }

        setSelection([]);
        isSelecting.current = false;
        startCell.current = null;
    }, [grid, words, foundWords]);

    // ── Event handlers ────────────────────────────────────────────────────────

    const onCellDown = (r, c) => {
        isSelecting.current = true;
        startCell.current = { r, c };
        setSelection([{ r, c }]);
    };

    const onCellEnter = (r, c) => {
        if (!isSelecting.current || !startCell.current) return;
        const cells = getCellsInLine(startCell.current, { r, c });
        setSelection(cells);
    };

    const onCellUp = (r, c) => {
        if (!isSelecting.current) return;
        const cells = getCellsInLine(startCell.current, { r, c });
        checkSelection(cells);
    };

    // Touch support
    const getGridCell = (touch, gridEl) => {
        const elements = document.elementsFromPoint(touch.clientX, touch.clientY);
        const cellEl = elements.find(el => el.dataset?.row !== undefined);
        if (!cellEl) return null;
        return { r: parseInt(cellEl.dataset.row), c: parseInt(cellEl.dataset.col) };
    };

    const onTouchStart = (e, r, c) => {
        e.preventDefault();
        onCellDown(r, c);
    };

    const onTouchMove = (e) => {
        e.preventDefault();
        const touch = e.touches[0];
        const cell = getGridCell(touch);
        if (cell) onCellEnter(cell.r, cell.c);
    };

    const onTouchEnd = (e) => {
        e.preventDefault();
        const touch = e.changedTouches[0];
        const cell = getGridCell(touch);
        if (cell) onCellUp(cell.r, cell.c);
        else {
            isSelecting.current = false;
            setSelection([]);
        }
    };

    if (!grid || grid.length === 0) return null;

    const cols = grid[0].length;

    return (
        <div style={styles.container}
            onMouseLeave={() => {
                if (isSelecting.current) {
                    isSelecting.current = false;
                    setSelection([]);
                }
            }}
        >
            <div style={styles.wrapper}>

                {/* SIATKA */}
                <div
                    style={{ ...styles.grid, gridTemplateColumns: `repeat(${cols}, 38px)` }}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                >
                    {grid.map((row, r) =>
                        row.map((letter, c) => {
                            const inSel  = isInSelection(r, c);
                            const conf   = isConfirmed(r, c);
                            const hinted = hintedCell?.r === r && hintedCell?.c === c;

                            let bg = '#ffffff';
                            if (conf)   bg = '#bbf7d0';
                            if (hinted) bg = '#fde68a'; // żółty puls dla hintu
                            if (inSel)  bg = '#93c5fd';

                            return (
                                <div
                                    key={cellKey(r, c)}
                                    data-row={r}
                                    data-col={c}
                                    style={{ ...styles.cell, backgroundColor: bg,
                                        transform: inSel || hinted ? 'scale(1.12)' : 'scale(1)',
                                        fontWeight: conf || inSel || hinted ? '800' : '600',
                                        color: conf ? '#064e3b' : inSel ? '#1e40af' : hinted ? '#92400e' : '#1e293b',
                                        boxShadow: inSel ? '0 0 0 2px #3b82f6' : conf ? '0 0 0 2px #059669' : hinted ? '0 0 0 3px #f59e0b' : 'none',
                                        transition: 'all 0.15s ease',
                                    }}
                                    onMouseDown={() => onCellDown(r, c)}
                                    onMouseEnter={() => onCellEnter(r, c)}
                                    onMouseUp={() => onCellUp(r, c)}
                                    onTouchStart={(e) => onTouchStart(e, r, c)}
                                >
                                    {letter}
                                </div>
                            );
                        })
                    )}
                </div>

                {/* PANEL SŁÓW */}
                <div style={styles.wordsPanel}>
                    <h3 style={styles.panelTitle}>Znajdź słowa</h3>
                    <div style={styles.wordsList}>
                        {words.map(w => {
                            const found = foundWords.has(w.answer.toUpperCase());
                            return (
                                <div key={w.answer} style={{
                                    ...styles.wordItem,
                                    textDecoration: found ? 'line-through' : 'none',
                                    color: found ? '#10b981' : '#1e293b',
                                    backgroundColor: found ? '#f0fdf4' : '#f8fafc',
                                    borderColor: found ? '#a7f3d0' : '#e2e8f0',
                                }}>
                                    <span style={styles.wordPolish}>{w.polish}</span>
                                    {!found && (
                                        <button
                                            title="Pokaż gdzie zaczyna się słowo"
                                            style={styles.hintBtn}
                                            onClick={() => showHint(w)}
                                        >
                                            🔍
                                        </button>
                                    )}
                                    {found && <span style={{ marginLeft: 'auto' }}>✅</span>}
                                </div>
                            );
                        })}
                    </div>
                    <div style={styles.progress}>
                        {foundWords.size} / {words.length} słów
                        <div style={styles.progressBar}>
                            <div style={{
                                ...styles.progressFill,
                                width: `${(foundWords.size / words.length) * 100}%`
                            }} />
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
}

const styles = {
    container: { width: '100%', userSelect: 'none', WebkitUserSelect: 'none' },
    wrapper: { display: 'flex', gap: '40px', alignItems: 'flex-start', flexWrap: 'wrap' },
    grid: {
        display: 'grid',
        gap: '3px',
        background: 'linear-gradient(135deg, #a4a5ff 0%, #8cb8ff 100%)',
        padding: '10px',
        borderRadius: '12px',
        width: 'max-content',
        cursor: 'default',
    },
    cell: {
        width: '38px', height: '38px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '16px',
        borderRadius: '6px',
        border: '1px solid #e2e8f0',
        transition: 'all 0.08s ease',
        cursor: 'crosshair',
    },
    wordsPanel: { flex: '0 0 260px' },
    panelTitle: { fontSize: '18px', fontWeight: '800', color: '#1e293b', marginBottom: '14px' },
    wordsList: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' },
    wordItem: {
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: '8px 12px', borderRadius: '8px', border: '1px solid',
        transition: 'all 0.2s',
    },
    wordPolish: { fontSize: '14px', fontWeight: '600', flex: 1 },
    hintBtn: {
        marginLeft: 'auto',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontSize: '16px',
        padding: '2px 4px',
        borderRadius: '6px',
        opacity: 0.6,
        transition: 'opacity 0.15s',
    },
    progress: { fontSize: '14px', fontWeight: '600', color: '#475569' },
    progressBar: {
        marginTop: '8px', height: '8px', backgroundColor: '#e2e8f0',
        borderRadius: '99px', overflow: 'hidden',
    },
    progressFill: {
        height: '100%', backgroundColor: '#2563eb',
        borderRadius: '99px', transition: 'width 0.4s ease',
    },
};
